'use client'

import { useEffect, useRef } from 'react'
import Script from 'next/script'
import type { User } from '@/lib/types'
import { usePathname } from 'next/navigation'
import {
  IS_PROD,
  GOOGLE_MEASUREMENT_ID,
  GOOGLE_ADS_TAG_ID,
  SOCIAL_PIXEL_ID,
  SOCIAL_TEST_EVENT_CODE,
  REDDIT_PIXEL_ID,
} from '@/lib/constant'
import {
  initMixpanelReplay,
  stopSessionRecording,
} from '@/lib/mixpanel/browser'
import { Analytics } from '@vercel/analytics/react'
import { SpeedInsights } from '@vercel/speed-insights/next'

// Declare global types for TypeScript
declare global {
  interface Window {
    gtag?: (...args: any[]) => void
    rdt?: (...args: any[]) => void
    fbq?: (...args: any[]) => void
  }
}

function IntercomMessenger({
  user,
  pathname,
}: {
  user?: User | null
  pathname: string | null
}) {
  const intercomInitialized = useRef(false)
  const intercomInstance = useRef<
    typeof import('@intercom/messenger-js-sdk').Intercom | null
  >(null)

  useEffect(() => {
    // Only run on client side and in production
    if (typeof window === 'undefined' || !IS_PROD) return

    // NEVER initialize Intercom on admin or changelog pages - internal systems
    const isAdminPage = pathname?.startsWith('/admin') ?? false
    const isChangelogPage = pathname?.startsWith('/changelog') ?? false
    if (isAdminPage || isChangelogPage) {
      return
    }

    // Check if we're on a /help page - only show launcher there
    const isHelpPage = pathname?.startsWith('/help') ?? false

    // Dynamically import Intercom SDK
    const initIntercom = async () => {
      try {
        let Intercom: typeof import('@intercom/messenger-js-sdk').Intercom

        // Only import SDK once
        const isFirstInit = !intercomInitialized.current
        if (isFirstInit) {
          const intercomModule = await import('@intercom/messenger-js-sdk')
          Intercom = intercomModule.Intercom
          intercomInstance.current = Intercom
          intercomInitialized.current = true

          console.log('Intercom SDK loaded successfully')
        } else {
          // SDK already loaded, use cached instance
          Intercom = intercomInstance.current!
        }

        // Base configuration - show launcher only on /help pages
        const baseConfig = {
          app_id: 'yysti6rn',
          hide_default_launcher: !isHelpPage,
          hide_notifications: !isHelpPage,
        }

        // Initialize or update Intercom configuration
        if (user) {
          Intercom({
            ...baseConfig,
            user_id: user._id.toString(),
            email: user.email,
            name: user.name || user.email,
            created_at: Math.floor(user.create_time.getTime() / 1000),
          })

          if (isFirstInit) {
            console.log('Intercom initialized with user data:', {
              user_id: user._id.toString(),
              email: user.email,
              showLauncher: isHelpPage,
            })
          } else {
            console.log('Intercom updated for pathname change:', {
              pathname,
              showLauncher: isHelpPage,
            })
          }
        } else {
          Intercom(baseConfig)

          if (isFirstInit) {
            console.log('Intercom initialized for anonymous user', {
              showLauncher: isHelpPage,
            })
          } else {
            console.log('Intercom updated for pathname change:', {
              pathname,
              showLauncher: isHelpPage,
            })
          }
        }
      } catch (error) {
        console.error('Failed to initialize/update Intercom:', error)
      }
    }

    initIntercom()

    // Note: We keep Intercom active across page navigations to maintain user session
    // and ensure user data stays synchronized. Only the launcher visibility changes.
  }, [user, pathname])

  // Cleanup on unmount (when user navigates away from the app entirely)
  useEffect(() => {
    return () => {
      // Note: We typically don't shutdown Intercom on component unmount
      // to maintain session continuity. Only shutdown if absolutely necessary.
    }
  }, [])

  return null
}

export function Integrations({ user }: { user?: User | null }) {
  const pathname = usePathname()

  // Check if current page is an internal system page (admin, changelog, dashboard)
  // These pages should NOT have any external tracking/analytics
  const isInternalPage =
    pathname?.startsWith('/admin') ||
    pathname?.startsWith('/changelog') ||
    pathname?.startsWith('/dashboard')

  // Initialize Mixpanel (Replay) client SDK
  useEffect(() => {
    if (isInternalPage) {
      // Ensure Replay does not run on internal pages
      stopSessionRecording()
      return
    }
    initMixpanelReplay(user).catch((e) =>
      console.error('Mixpanel init error:', e),
    )
  }, [user, pathname, isInternalPage])

  // 动态设置 Google Analytics 4 用户ID
  useEffect(() => {
    if (!GOOGLE_MEASUREMENT_ID || isInternalPage) return

    const setGA4UserIdWhenReady = () => {
      if (window.gtag) {
        try {
          if (user?._id) {
            window.gtag('config', GOOGLE_MEASUREMENT_ID, {
              user_id: user._id.toString(),
            })
          } else {
            window.gtag('config', GOOGLE_MEASUREMENT_ID, {
              user_id: null,
            })
          }
        } catch (error) {
          console.error('GA4 user ID setting error:', error)
        }
      } else {
        setTimeout(setGA4UserIdWhenReady, 100)
      }
    }

    setGA4UserIdWhenReady()
  }, [user, isInternalPage])

  // 动态设置 Google Ads Tag 用户ID
  useEffect(() => {
    if (!GOOGLE_ADS_TAG_ID || isInternalPage) return

    const setGoogleAdsUserIdWhenReady = () => {
      if (window.gtag) {
        try {
          if (user?._id) {
            window.gtag('config', GOOGLE_ADS_TAG_ID, {
              user_id: user._id.toString(),
            })
          } else {
            window.gtag('config', GOOGLE_ADS_TAG_ID, {
              user_id: null,
            })
          }
        } catch (error) {
          console.error('Google Ads Tag user ID setting error:', error)
        }
      } else {
        setTimeout(setGoogleAdsUserIdWhenReady, 100)
      }
    }

    setGoogleAdsUserIdWhenReady()
  }, [user, isInternalPage])

  // 设置 Social Pixel 用户ID
  // IMPORTANT: fbq('init') should only be called once (in the Script tag)
  // Use fbq('set') to update user data dynamically instead of re-initializing
  useEffect(() => {
    if (!SOCIAL_PIXEL_ID || isInternalPage) return

    const setSocialUserIdWhenReady = () => {
      if (window.fbq) {
        try {
          if (user?._id) {
            // Use fbq('set') instead of fbq('init') to update user data
            // This prevents double initialization issues
            window.fbq('set', 'userData', {
              external_id: user._id.toString(),
              em: user.email, // Social会自动哈希邮箱
            })
          }
          // If user is null/undefined, we don't need to do anything
          // Social Pixel will continue working without user data
        } catch (error) {
          console.error('Social Pixel user ID setting error:', error)
        }
      } else {
        setTimeout(setSocialUserIdWhenReady, 100)
      }
    }

    setSocialUserIdWhenReady()
  }, [user, isInternalPage])

  // 设置 Reddit Pixel 用户ID
  // Non-prod environments will send test data
  useEffect(() => {
    if (!REDDIT_PIXEL_ID || isInternalPage) return

    const setRedditUserIdWhenReady = () => {
      if (window.rdt) {
        try {
          if (user?._id) {
            window.rdt('track', 'PageVisit', {
              userId: user._id.toString(),
              email: user.email, // Reddit会处理哈希
            })
          }
          // 用户登出时，Reddit Pixel会通过session自动管理
        } catch (error) {
          console.error('Reddit Pixel user ID setting error:', error)
        }
      } else {
        setTimeout(setRedditUserIdWhenReady, 100)
      }
    }

    setRedditUserIdWhenReady()
  }, [user, isInternalPage])

  return (
    <>
      {/* Google Tag (gtag.js) - Load once, configure both GA4 and Google Ads */}
      {/* gtag.js is a universal script that can handle both GA4 (G-*) and Google Ads (AW-*) tags */}
      {(GOOGLE_MEASUREMENT_ID || GOOGLE_ADS_TAG_ID) && (
        <>
          {/* Load gtag.js script once - use GA4 ID if available, otherwise use Google Ads ID */}
          <Script
            src={`https://www.googletagmanager.com/gtag/js?id=${
              GOOGLE_MEASUREMENT_ID || GOOGLE_ADS_TAG_ID
            }`}
            strategy="afterInteractive"
          />
          <Script id="google-tag-init" strategy="afterInteractive">
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              
              ${
                GOOGLE_MEASUREMENT_ID
                  ? `
              // Configure Google Analytics 4
              gtag('config', '${GOOGLE_MEASUREMENT_ID}', {
                send_page_view: true,
                debug_mode: ${!IS_PROD}
              });
              `
                  : ''
              }
              
              ${
                GOOGLE_ADS_TAG_ID
                  ? `
              // Configure Google Ads Tag
              gtag('config', '${GOOGLE_ADS_TAG_ID}', {
                send_page_view: false,
                debug_mode: ${!IS_PROD}
              });
              `
                  : ''
              }
            `}
          </Script>
        </>
      )}

      {/* 其他第三方脚本 */}
      <Script
        strategy="afterInteractive"
        data-website-id="dfid_KUwKYL8Qnci4O7FP2I3qA"
        data-domain="heyboss.ai"
        src="https://datafa.st/js/script.js"
      />
      <Script src="https://r.wdfl.co/rw.js" data-rewardful="86b937" />
      <Script id="rewardful-queue" strategy="beforeInteractive">
        {`(function(w,r){w._rwq=r;w[r]=w[r]||function(){(w[r].q=w[r].q||[]).push(arguments)}})(window,'rewardful');`}
      </Script>

      {IS_PROD && (
        <Script
          defer
          data-domain="heyboss.xyz"
          src="https://plausible.io/js/script.outbound-links.js"
        />
      )}

      <Script>
        {`window.plausible = window.plausible || function () {
            (window.plausible.q = window.plausible.q || []).push(arguments)
          }`}
      </Script>

      {IS_PROD && (
        <Script type="text/javascript">
          {`(function(c,l,a,r,i,t,y){
            c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
        })(window, document, "clarity", "script", "${process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID}");`}
        </Script>
      )}

      {/* Social Pixel */}
      {/* Non-prod environments will use test event code for debugging */}
      {SOCIAL_PIXEL_ID && (
        <>
          <Script id="social-pixel" strategy="afterInteractive">
            {`
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.social.net/en_US/fbevents.js');
              fbq('init', '${SOCIAL_PIXEL_ID}'${!IS_PROD && SOCIAL_TEST_EVENT_CODE ? `, {}, {testEventCode: '${SOCIAL_TEST_EVENT_CODE}'}` : ''});
              fbq('track', 'PageView');
            `}
          </Script>
          <noscript>
            <img
              height="1"
              width="1"
              style={{ display: 'none' }}
              src={`https://www.social.com/tr?id=${SOCIAL_PIXEL_ID}&ev=PageView&noscript=1`}
              alt=""
            />
          </noscript>
        </>
      )}

      {/* Reddit Pixel */}
      {/* Non-prod environments will send test data for debugging */}
      {REDDIT_PIXEL_ID && (
        <Script id="reddit-pixel" strategy="afterInteractive">
          {`
            !function(w,d){if(!w.rdt){var p=w.rdt=function(){p.sendEvent?p.sendEvent.apply(p,arguments):p.callQueue.push(arguments)};p.callQueue=[];
            var t=d.createElement("script");t.src="https://www.redditstatic.com/ads/pixel.js",t.async=!0;
            var s=d.getElementsByTagName("script")[0];s.parentNode.insertBefore(t,s)}}(window,document);
            rdt('init','${REDDIT_PIXEL_ID}');
            rdt('track', 'PageVisit');
          `}
        </Script>
      )}

      {/* LinkedIn Pixel */}
      {IS_PROD && (
        <>
          <Script id="linkedin-pixel" strategy="afterInteractive">
            {`
              _linkedin_partner_id = "7450068";
              window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
              window._linkedin_data_partner_ids.push(_linkedin_partner_id);
            `}
          </Script>
          <Script id="linkedin-pixel-loader" strategy="afterInteractive">
            {`
              (function(l) {
                if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
                window.lintrk.q=[]}
                var s = document.getElementsByTagName("script")[0];
                var b = document.createElement("script");
                b.type = "text/javascript";b.async = true;
                b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
                s.parentNode.insertBefore(b, s);
              })(window.lintrk);
            `}
          </Script>
          <noscript>
            <img
              height="1"
              width="1"
              style={{ display: 'none' }}
              src="https://px.ads.linkedin.com/collect/?pid=7450068&fmt=gif"
              alt=""
            />
          </noscript>
        </>
      )}

      {/* Twitter Pixel */}
      {IS_PROD && (
        <Script id="twitter-pixel" strategy="afterInteractive">
          {`
            !function(e,t,n,s,u,a){e.twq||(s=e.twq=function(){s.exe?s.exe.apply(s,arguments):s.queue.push(arguments);
            },s.version='1.1',s.queue=[],u=t.createElement(n),u.async=!0,u.src='https://static.ads-twitter.com/uwt.js',
            a=t.getElementsByTagName(n)[0],a.parentNode.insertBefore(u,a))}(window,document,'script');
            twq('config','p2x1f');
          `}
        </Script>
      )}

      {/* PostHog Analytics - only enabled in production */}
      {IS_PROD && process.env.NEXT_PUBLIC_POSTHOG_KEY && (
        <Script id="posthog-init" strategy="afterInteractive">
          {`
            !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init Ie Ts Ms Ee Es Rs capture Ge calculateEventProperties Os register register_once register_for_session unregister unregister_for_session js getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSurveysLoaded onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey canRenderSurveyAsync identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty Ds Fs createPersonProfile Ls Ps opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing Cs debug I As getPageViewId captureTraceFeedback captureTraceMetric".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
            posthog.init('${process.env.NEXT_PUBLIC_POSTHOG_KEY}', {
              api_host: '${process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.posthog.com'}',
              person_profiles: 'identified_only',
              autocapture: true,
              capture_pageview: true,
              capture_pageleave: true,
            });
          `}
        </Script>
      )}

      {/* Intercom Messenger */}
      {/* Initialize Intercom on all pages for user data sync, but only show launcher on /help pages */}
      {/* 🔴🔴🔴 📸 UPDATE INTERCOM ARTICLES + ADD SCREENSHOT! 🔴🔴🔴 */}
      {IS_PROD && <IntercomMessenger user={user} pathname={pathname} />}
      <SpeedInsights />
      <Analytics />
    </>
  )
}
